import React, { useMemo, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import Card from "../ui/Card";
import { useI18n } from "../../i18n/I18nProvider";
import { buildWeekDays, formatWeekLabel } from "../../lib/sampleWeek";

const WeekBoard = ({ plain = false, compact = false, story = false }) => {
  const { t, lang } = useI18n();
  const reduced = useReducedMotion();
  const days = useMemo(() => buildWeekDays(t, lang), [t, lang]);
  const [swapped, setSwapped] = useState({});
  const filters = t("rows.calendar.filters");
  const reminders = t("rows.calendar.reminders");

  if (story) {
    return (
      <div className="rounded-card border border-hairline bg-canvas p-4 md:p-5">
        <div className="flex flex-col items-start gap-1 sm:flex-row sm:items-baseline sm:justify-between sm:gap-3">
          <p className="text-body font-medium text-ink">{formatWeekLabel(t, lang)}</p>
          <p className="text-caption text-mid-gray">{t("rows.calendar.detailMeta")}</p>
        </div>
        <ul className="mt-3 flex flex-wrap gap-2 p-0">
          {filters.map((filter) => (
            <li
              key={filter}
              className="list-none rounded-pill bg-lilac px-3 py-1 text-caption text-[#241c3e]"
            >
              {filter}
            </li>
          ))}
        </ul>
        <div className="mt-4 grid grid-cols-7 gap-1">
          {days.map((day) => (
            <div
              key={day.key}
              className={`rounded-nested border px-0.5 py-2 text-center ${
                day.today
                  ? "border-accent bg-accent text-white"
                  : "border-hairline bg-paper text-ink"
              }`}
            >
              <p className="text-[10px] font-medium leading-none sm:text-[11px]">{day.name}</p>
              <p className="mt-2 text-body font-semibold leading-none">{day.date}</p>
              <span
                className={`mx-auto mt-2 block h-1.5 w-1.5 rounded-full ${
                  day.chips.length ? (day.today ? "bg-white" : "bg-accent") : "bg-transparent"
                }`}
              />
            </div>
          ))}
        </div>
        <div className="mt-4 rounded-nested border border-hairline bg-paper p-4">
          <p className="text-caption font-medium text-accent">{t("rows.calendar.detailWhen")}</p>
          <p className="mt-1 text-subheading font-semibold text-ink">
            {t("rows.calendar.detailTitle")}
          </p>
          <p className="mt-1 text-caption text-mid-gray">{t("rows.calendar.detailMeta")}</p>
          <p className="mt-4 text-caption font-medium text-ink">{t("rows.calendar.reminderLabel")}</p>
          <ul className="mt-2 flex flex-wrap gap-2 p-0">
            {reminders.map((item) => (
              <li
                key={item}
                className="list-none rounded-pill border border-hairline px-3 py-1 text-caption text-ink"
              >
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  }

  const Frame = plain ? "div" : Card;
  const board = (
    <Frame
      {...(plain ? {} : { padding: false })}
      className={plain ? "" : "overflow-hidden rounded-stage p-4 shadow-card md:p-5"}
    >
      <p className="mb-4 text-body font-medium text-mid-gray">
        {formatWeekLabel(t, lang)}
      </p>
      <div className={`flex gap-2 pb-1 ${compact ? "w-max" : "overflow-x-auto lg:grid lg:grid-cols-7 lg:overflow-visible"}`}>
        {days.map((day, index) => {
          const chips = swapped[day.key] ? [t("week.chipSwap")] : day.chips;
          return (
            <motion.button
              key={day.key}
              type="button"
              onClick={() =>
                setSwapped((current) => ({
                  ...current,
                  [day.key]: !current[day.key],
                }))
              }
              initial={reduced ? false : { opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.4, delay: index * 0.06, ease: [0.22, 1, 0.36, 1] }}
              className={`flex min-h-[168px] w-[148px] shrink-0 flex-col rounded-nested border p-3 text-left transition-transform hover:-translate-y-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${compact ? "" : "lg:w-auto"} ${
                day.today
                  ? "border-accent bg-accent text-white"
                  : "border-hairline bg-canvas text-ink"
              }`}
            >
              <span className="flex items-baseline justify-between gap-2">
                <span className="text-caption font-medium">{day.name}</span>
                <span className="text-heading-sm font-semibold tracking-heading-sm">
                  {day.date}
                </span>
              </span>
              {day.today ? (
                <span className="mt-1 text-caption opacity-80">{t("week.today")}</span>
              ) : null}
              <span className="mt-3 flex flex-1 flex-col gap-1.5">
                {chips.length === 0 ? (
                  <span className={`text-caption ${day.today ? "opacity-70" : "text-mid-gray"}`}>
                    {t("week.empty")}
                  </span>
                ) : (
                  chips.map((chip) => (
                    <span
                      key={chip}
                      className={`rounded-nested px-2 py-1 text-caption font-medium ${
                        day.today ? "bg-white/15 text-white" : "bg-paper text-ink"
                      }`}
                    >
                      {chip}
                    </span>
                  ))
                )}
              </span>
            </motion.button>
          );
        })}
      </div>
    </Frame>
  );

  return board;
};

export default WeekBoard;
